import type { Location as LocationHistory } from 'history';
import { deriveQueryStringHelpersFromLocation, qs } from '@shared/core/queryString';
import type { FeatureFlags } from './featureFlags.constants';
import type { FeatureFlagsState, FeatureFlagsContextType, FeatureFlagKey } from './featureFlags.types';
import type { Draft } from 'immer';

const _isSupportedValue = (supportedValues: ReadonlyArray<string> | ReadonlyArray<boolean> | boolean, value: Maybe<string | boolean>) => {
  return !!value && Array.isArray(supportedValues)
    ? supportedValues.includes(value)
    : // When `supportedValues` is  of type boolean, we accept all non-empty string values
      true;
};

export const updateStateDraftFromLocation = (config: FeatureFlags, stateDraft: Draft<FeatureFlagsState>, location: Maybe<Location | LocationHistory>): void => {
  const { getValueString } = deriveQueryStringHelpersFromLocation(location);
  Object.entries(config).forEach(([feature, { queryParam, supportedValues, type }]) => {
    const storedValue = stateDraft[feature as FeatureFlagKey];
    const rawValue = getValueString(queryParam);
    const maybeValue = type === 'boolean' ? rawValue === 'true' : rawValue;

    // Prefer new value if valid, otherwise default to the current (string | undefined)
    // @ts-expect-error Object.entries widens the key to string, so it no longer indexes the state type
    stateDraft[feature] = rawValue !== undefined && _isSupportedValue(supportedValues, maybeValue) ? maybeValue : storedValue;
  });
};

export const deriveFeatureContextFromState = (config: FeatureFlags, state: FeatureFlagsState) => {
  return Object.entries(config).reduce(
    (acc, [feature, featureValue]) => {
      // @ts-expect-error Object.entries widens the key to string, so it no longer indexes the context type
      acc[feature] = {
        ...featureValue,
        // @ts-expect-error Object.entries widens the key to string, so it no longer indexes the state type
        value: state[feature],
      };
      return acc;
    },
    {} as FeatureFlagsContextType['features'],
  );
};

const _transformStateToQueryObject = (config: FeatureFlags, state: FeatureFlagsState) => {
  return Object.entries(config).reduce(
    (acc, [feature, { queryParam }]) => {
      // @ts-expect-error Object.entries widens the key to string, so it no longer indexes the state type
      acc[queryParam] = state[feature];
      return acc;
    },
    {} as Record<string, string>,
  );
};

/**
 * The feature flag query object is the same for every url formatted against a given config and
 * state, so build it once here and reuse it for each url the returned formatter is given.
 */
export const createFeatureFlagsUrlFormatter = (config: FeatureFlags, state: FeatureFlagsState) => {
  const featureFlagsQuery = _transformStateToQueryObject(config, state);
  // `query-string` drops undefined values, so a state where no flag is set adds nothing to any url.
  // That is every visitor who has not put a `feature.*` param in their address bar.
  const hasFlagToAppend = Object.values(featureFlagsQuery).some(value => value !== undefined);

  return (url: string = '') => {
    // Only skip the round trip for urls with neither, because `stringifyUrl` is not an identity
    // function: it sorts query keys and re-encodes both the query and the fragment. It does return
    // everything before the first `?` or `#` verbatim, which makes this case exactly equivalent.
    if (!hasFlagToAppend && !url.includes('?') && !url.includes('#')) {
      return url;
    }
    const { url: providedUrl, fragmentIdentifier, query } = qs.parseUrl(url, { parseFragmentIdentifier: true });
    return qs.stringifyUrl({ url: providedUrl, query: { ...featureFlagsQuery, ...query }, fragmentIdentifier });
  };
};

export const formatFeatureFlagsToUrl = (config: FeatureFlags, state: FeatureFlagsState, url: string = '') => {
  return createFeatureFlagsUrlFormatter(config, state)(url);
};
